/**
 * The app wrapper (1.Y deliverable 11): `ComposedWrapper` is the root's `Wrapper` (1.I-1's
 * `AppRoot`, `routes/__root.tsx`): `<ComposeProviders><OpenModes>{children}</OpenModes>
 * </ComposeProviders>`. The providers sit outside 1.I-2's `OpenModes` because it renders the peeks
 * beside its children, so the page, every peek and the split pane, and `/reference/$id` are all
 * covered. 1.I-1's `AppProviders` is untouched.
 *
 * `ComposeProviders`: `CustomizeProvider` (1.AC-2's dialog and the handlers) around 1.AB-2's
 * `ReferenceActionsProvider`, whose `onCustomize` is `fromReference` only while `reference` and
 * `statblock_form` are both on (flags from the shell's session through `safeFlags`, every flag off
 * while it is null or malformed);
 * otherwise no reference page shows "Customize".
 */
import type { ReactNode } from 'react';
import { OpenModes } from '../nav/index.js';
import { ReferenceActionsProvider } from '../reference/actions.js';
import { safeFlags } from '../shell/frame-rule.js';
import { useShellSession } from '../shell/session.js';
import { CustomizeProvider, useCustomizeHandlers } from './customize.js';

function ReferenceActions({ children }: { readonly children?: ReactNode }) {
  const me = useShellSession();
  // Fail-closed: a malformed auth.me answer turns every flag off instead of throwing above every route.
  const flags = safeFlags(me);
  const handlers = useCustomizeHandlers();
  const on = flags?.reference === true && flags.statblock_form;
  return (
    <ReferenceActionsProvider onCustomize={on ? handlers?.fromReference : undefined}>
      {children}
    </ReferenceActionsProvider>
  );
}

export function ComposeProviders({ children }: { readonly children?: ReactNode }) {
  return (
    <CustomizeProvider>
      <ReferenceActions>{children}</ReferenceActions>
    </CustomizeProvider>
  );
}

export function ComposedWrapper({ children }: { readonly children: ReactNode }) {
  return (
    <ComposeProviders>
      <OpenModes>{children}</OpenModes>
    </ComposeProviders>
  );
}
